/*eslint-disable max-lines*/
import {
  useResolveLocationAlarm,
  useScanAlarmActionBackup,
} from '@hakimo-ui/hakimo/data-access';

import {
  CamAction,
  StatusType,
  UNIFIED_DATA_VERSION,
  UnifiedAlarmGroup,
  UnifiedMonitoringOutboundMessage,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  trackEvent,
  useUnifiedMonitoringWs,
  useUser,
} from '@hakimo-ui/hakimo/util';
import { useEffect, useState } from 'react';
import { getEventTypeFromCamAction } from '../utils';
import { CAM_ACTION_TRACKING_EVENT } from './constants';
import { MultiAlarmGroupView } from './MultiAlarmGroupView';
import { SingleAlarmGroupView } from './SingleAlarmGroupView';

interface Props {
  alarmGroupIdToDetailsMap: Record<string, UnifiedAlarmGroup>;
  totalAlarmCount: number;
  navigateToPendingAlarms: () => void;
  onInvestigateAlarmChange?: (id: string | null) => void;
}
export function UnifiedMonitoringAlarms(props: Props) {
  const {
    alarmGroupIdToDetailsMap,
    totalAlarmCount,
    navigateToPendingAlarms,
    onInvestigateAlarmChange,
  } = props;
  const [investigateAlarmGroupId, setInvestigateAlarmGroupId] = useState<
    string | null
  >(null);

  useEffect(() => {
    onInvestigateAlarmChange?.(investigateAlarmGroupId);
  }, [investigateAlarmGroupId, onInvestigateAlarmChange]);

  const resolveLocationAlarm = useResolveLocationAlarm();
  // Backup REST path for SCAN actions in case the WS message is dropped.
  // Fired alongside the WS send; the gateway applies the same effect idempotently.
  const scanActionBackup = useScanAlarmActionBackup();

  const user = useUser();
  const { send } = useUnifiedMonitoringWs();

  const onAlarmGroupAction = (
    actionType: CamAction,
    alarmGroupId: string,
    comment?: string,
    cameraId?: string,
    cameraGroupId?: string,
    action?: string, // action and subaction is for resolve and escalate actions (Multi level actions)
    subAction?: string,
    reason?: string,
    category?: string
  ) => {
    //safe, escalate_open, escalate_close
    const alarmGroup = alarmGroupIdToDetailsMap[alarmGroupId];
    const product = alarmGroup.product;
    const isAlreadyResolved = alarmGroup.status
      .toLowerCase()
      .includes('resolved');
    trackEvent(CAM_ACTION_TRACKING_EVENT[actionType], {
      alarmGroupId,
      product,
      wasAlreadyResolved: isAlreadyResolved,
    });
    if (actionType === CamAction.INVESTIGATE) {
      setInvestigateAlarmGroupId(alarmGroupId);
      return;
    }
    if (product === UnifiedMonitoringProduct.SCAN && !isAlreadyResolved) {
      const message: UnifiedMonitoringOutboundMessage = {
        alarm_group_id: alarmGroupId,
        camera_id: cameraId ?? '',
        group_id: cameraGroupId ?? '',
        tenant_id: alarmGroup.tenantId,
        event_type: getEventTypeFromCamAction(actionType),
        product: alarmGroup.product,
        additional_data: {
          username: user.email,
          comment: comment ?? '',
          resolution_reason: reason ?? '',
          incident_category: category ?? '',
          situation: action ?? '',
          situation_description: subAction ?? '',
        },
      };
      send?.(message);
      // Backup REST call alongside the WS message — best-effort, non-blocking.
      // Only the three explicitly-supported actions get a backup;
      // getEventTypeFromCamAction() defaults unknown actions to ALARM_SAFE, so
      // gate on actionType here to avoid firing a stray "safe" backup.
      if (
        actionType === CamAction.SAFE ||
        actionType === CamAction.ESCALATE ||
        actionType === CamAction.RESOLVE_ESCALATION
      ) {
        scanActionBackup.mutate(message);
      }
    } else if (
      (product === UnifiedMonitoringProduct.REMOTE_GUARDING ||
        product === UnifiedMonitoringProduct.ENTERPRISE) &&
      !isAlreadyResolved
    ) {
      if (actionType === CamAction.SAFE) {
        // Enterprise alarms are location-alarms under the hood, so they're
        // resolved via the same remote-guarding endpoint (matches
        // useUpdateLocationAlarm's default, used by the SOP-view enterprise
        // resolve flow).
        resolveLocationAlarm(
          alarmGroup.id,
          UnifiedMonitoringProduct.REMOTE_GUARDING,
          {
            status: StatusType.RESOLVED,
            comment: 'safe',
          },
          undefined,
          alarmGroup.unificationEnabled ? UNIFIED_DATA_VERSION : undefined
        );
      }
    }

    const isSingleAlarmView = totalAlarmCount === 1 || investigateAlarmGroupId;

    if (
      actionType === CamAction.SAFE &&
      isSingleAlarmView
      // actionType === CamAction.RESOLVE_ESCALATION
    ) {
      navigateToPendingAlarms();
    }
  };

  return totalAlarmCount === 1 || investigateAlarmGroupId ? (
    <SingleAlarmGroupView
      alarmGroup={
        investigateAlarmGroupId
          ? alarmGroupIdToDetailsMap[investigateAlarmGroupId]
          : Object.values(alarmGroupIdToDetailsMap)[0]
      }
      onAlarmGroupAction={onAlarmGroupAction}
      navigateToPendingAlarms={navigateToPendingAlarms}
    />
  ) : (
    <MultiAlarmGroupView
      alarmGroupIdToDetailsMap={alarmGroupIdToDetailsMap}
      totalAlarmCount={totalAlarmCount}
      onAlarmGroupAction={onAlarmGroupAction}
      navigateToPendingAlarms={navigateToPendingAlarms}
    />
  );
}
